import { useState } from 'react';
import Required from '../../../common-components/Required';
import CloseButton from '../../../custom-components/CloseButton';
import CancelButton from '../../../custom-components/CancelButton';
import BlackButton from '../../../custom-components/BlackButton';
import { useDispatch, useSelector } from 'react-redux';
import {
  fileDelete,
  fileUpload,
  multipleFileUpload,
  previewImage,
  progressPercentage,
  resetMediaErrorAlert,
  resetMultipleFileUpload,
  setSelectedFile,
} from '../../../../slices/FileUploadSlice';
import FileUploader from '../../../common-components/FileUploader';
import { RechargeStringLabels } from '../../../../util/Strings';
import {
  setPreThumbnailId,
  setPrevHotelIds,
  setPrevHotelImageDetails,
  setPrevThumbnailDetails,
} from '../../../../slices/HotelSlice';
import ImagePreviewWithFilename from '../../../common-components/ImagePreviewWithFilename';
import { NewRechargeFormValidations } from '../../../../util/Validations';
import ErrorMessageComponent from '../../../common-components/ErrorMessageComponent';
import useFileValidation from '../../../../util/useFileValidation';
import ErrorAlert from '../../../common-components/ErrorAlert';
import ErrorMessages from '../../../../util/ErrorMessages';

function AddHotelForm({ onClickSubmit, show, data }) {
  const dispatch = useDispatch();

  // Normalize arrays and preview list
  const initialHotelImageIds = data?.hotelImageIds ?? [];
  const multipleFileNameWithImageUrls = [];
  if (data && data.hotelImageUrls && data.photoFileNames) {
    for (let i = 0; i < data.hotelImageUrls.length; i++) {
      multipleFileNameWithImageUrls.push({
        photoUrl: data.hotelImageUrls[i],
        fileName: data.photoFileNames[i],
        id: data.hotelImageIds[i],
      });
    }
  }

  const [state, setState] = useState({
    name: data?.name ?? '',
    description: data?.description ?? '',
    location: data?.location ?? '',
    address: data?.address ?? '',
    latitude: data?.latitude ?? '',
    longitude: data?.longitude ?? '',
    drivingTime: data?.drivingTime ?? '',
    hotelThumbnailId: data?.hotelThumbnailId ?? null,
    hotelImageIds: initialHotelImageIds,
  });

  const [prevFileData, setPreFileData] = useState({
    photoUrl: data?.hotelThumbnailUrl ?? null,
    fileName: data?.thumbnailFileName ?? null,
  });

  const [prevFileId, setPrevFileId] = useState(data?.hotelThumbnailId ?? null);
  const [prevMultipleFileIds, setPrevMultipleFileIds] = useState(initialHotelImageIds);
  const [prevMultiFileData, setPrevMultiFileData] = useState(multipleFileNameWithImageUrls ?? []);
  const [imageDeleteList, setImageDeleteList] = useState([]);
  const [validation, setValidation] = useState({});

  const prevThumbnailId = useSelector((s) => s.hotel.prevThumbnailId);
  const prevHotelIds = useSelector((s) => s.hotel.prevHotelIds);
  const errorMessageCode = useSelector((s) => s.upload.errorCode);
  // these selectors come from hotel slice
  const prevRechargeMultipleFileData = useSelector((s) => s.hotel.prevHotelImageDetails);
  const prevRechargeThumbnailFileData = useSelector((s) => s.hotel.prevThumbnailDetails);

  const {
    errorSingleMessage,
    setErrorSingleMessage,
    errorMultiMessage,
    setErrorMultiMessage,
    validateFile,
    validateFiles,
  } = useFileValidation();

  function handleCloseErrorAlert() {
    dispatch(resetMediaErrorAlert(false));
    setPrevMultiFileData(prevRechargeMultipleFileData ?? multipleFileNameWithImageUrls);
    handleMultipleImageState(); // sync state with prevMultipleFileIds
    dispatch(setSelectedFile(null));
    setPreFileData(prevRechargeThumbnailFileData ?? { photoUrl: data?.hotelThumbnailUrl ?? null, fileName: data?.thumbnailFileName ?? null });
    handleState();
    dispatch(setSelectedFile(null));
    setImageDeleteList([]);
    onClickSubmit();
  }

  const checkValidation = () => {
    const { errors, isValid } = NewRechargeFormValidations(state);
    setValidation(errors);
    return isValid;
  };

  function handleChange(e) {
    const value = e.target.value;
    setState((prev) => ({ ...prev, [e.target.name]: value }));
  }

  function resetImage() {
    dispatch(progressPercentage(0));
    dispatch(setSelectedFile(null));
    dispatch(previewImage(null));
    dispatch(resetMultipleFileUpload());
  }

  // ===== Safe handlers =====
  function handleImageCancel(photoId) {
    setPrevMultiFileData((prev) => (Array.isArray(prev) ? prev.filter((image) => image.id !== photoId) : prev));
    setPrevMultipleFileIds((prevIds) => {
      const ids = Array.isArray(prevIds) ? prevIds.filter((id) => id !== photoId) : [];
      setState((prevState) => ({ ...prevState, hotelImageIds: ids }));
      return ids;
    });
    setImageDeleteList((prev) => [...prev, photoId]);
  }

  function handleThumbnailCancel() {
    setState((prev) => ({ ...prev, hotelThumbnailId: null }));
    setPreFileData({ photoUrl: null, fileName: null });
    setPrevFileId((currentPrevFileId) => {
      if (currentPrevFileId) {
        setImageDeleteList((prev) => [...prev, currentPrevFileId]);
      }
      return null;
    });
  }

  // --- MAIN CHANGES HERE ----
  async function handleNextButton() {
    if (checkValidation()) {
      if (
        state.name &&
        state.location &&
        state.drivingTime &&
        state.address &&
        state.latitude &&
        state.longitude &&
        state.description
      ) {
        const newData = state;
        if (data) {
          // normalize saved state snapshot
          setState({
            name: newData.name,
            description: newData.description,
            location: newData.location,
            address: newData.address,
            latitude: newData.latitude,
            longitude: newData.longitude,
            drivingTime: newData.drivingTime,
            hotelThumbnailId: newData.hotelThumbnailId,
            hotelImageIds: newData.hotelImageIds,
          });

          // Await deleting images
          if (imageDeleteList && imageDeleteList.length) {
            await Promise.all(
              imageDeleteList.map((imageId) =>
                dispatch(fileDelete({ id: imageId, isVideo: false, isMulti: true })).unwrap()
              )
            );
          }

          resetImage();

          // Await onClickSubmit for update & getHotel API calls
          if (onClickSubmit) {
            await onClickSubmit(newData, data._id);
          }

          if (state.hotelThumbnailId !== prevThumbnailId && prevThumbnailId !== '') {
            const id = { id: prevThumbnailId, isVideo: false };
            await dispatch(fileDelete(id)).unwrap();
            dispatch(setPreThumbnailId(''));
          }

          setPreFileData({
            photoUrl: data?.hotelThumbnailUrl ?? null,
            fileName: data?.thumbnailFileName ?? null,
          });
          setImageDeleteList([]);
          setErrorSingleMessage('');
          setErrorMultiMessage('');
        } else {
          // New record
          setState({});
          resetImage();
          setImageDeleteList([]);
          if (onClickSubmit) {
            await onClickSubmit(newData);
          }
        }
      } else {
        if (onClickSubmit) {
          await onClickSubmit();
        }
      }
    }
  }
  // ----------------------------

  if (!show) {
    return null;
  }

  function handleState() {
    setState((prev) => ({ ...prev, hotelThumbnailId: data?.hotelThumbnailId ?? null }));
  }

  function handleMultipleImageState(fileId) {
    if (fileId === undefined) {
      setState((prevState) => ({ ...prevState, hotelImageIds: prevMultipleFileIds }));
      return;
    }
    setPrevMultipleFileIds((prevIds) => {
      const ids = Array.isArray(prevIds) ? prevIds.filter((id) => id !== fileId) : [];
      setState((prevState) => ({ ...prevState, hotelImageIds: ids }));
      return ids;
    });
    setPrevMultiFileData((prev) => (Array.isArray(prev) ? prev.filter((img) => img.id !== fileId) : prev));
  }

  const handleFileSelect = function (event) {
    const files = event.target.files;
    postFileData(files);
  };

  const handleMultiFileSelect = function (event) {
    const files = event.target.files;
    postMultipleFileData(files);
  };

  const handleDrag = function (event) {
    event.preventDefault();
  };

  const handleDrop = function (event) {
    event.preventDefault();
    const files = event.dataTransfer.files;
    postFileData(files);
  };

  const handleMultiDrop = function (event) {
    event.preventDefault();
    const files = event.dataTransfer.files;
    postMultipleFileData(files);
  };

  function postMultipleFileData(files) {
    const valid = validateFiles(files);
    if (valid) {
      if (prevMultiFileData && prevMultiFileData.length) {
        dispatch(setPrevHotelIds(prevMultipleFileIds));
        dispatch(setPrevHotelImageDetails(prevMultiFileData));
      }

      dispatch(multipleFileUpload(files))
        .unwrap()
        .then((res) => {
          const newIds = res.map((r) => r._id);
          setPrevMultipleFileIds((prevIds) => {
            const ids = Array.isArray(prevIds) ? prevIds.concat(newIds) : newIds;
            setState((prevState) => ({ ...prevState, hotelImageIds: ids }));
            return ids;
          });
        })
        .catch((err) => {
          console.debug('multipleFileUpload failed', err);
        });
    }
  }

  function postFileData(files) {
    const valid = validateFile(files);
    if (valid) {
      const file = files[0];
      if (prevFileData && (prevFileData.photoUrl || prevFileData.fileName)) {
        dispatch(setPreThumbnailId(prevFileId));
        setPrevFileId((curr) => curr);
        dispatch(setPrevThumbnailDetails(prevFileData));
        setPreFileData({ photoUrl: null, fileName: null });
      }
      if (file) {
        dispatch(setSelectedFile(file));
        dispatch(fileUpload(file))
          .unwrap()
          .then((res) => {
            setState((prevState) => ({ ...prevState, hotelThumbnailId: res[0]._id }));
            setPrevFileId(res[0]._id);
          })
          .catch((err) => {
            console.debug('fileUpload failed', err);
          });
      }
    }
  }

  if (errorMessageCode) {
    return (
      <ErrorAlert
        show={errorMessageCode}
        onClickHandle={handleCloseErrorAlert}
        title="Error"
        subTitle={ErrorMessages(errorMessageCode)}
      />
    );
  }

  return (
    <div className="modal">
      <div className="form-scroll">
        <div className="form-flex">
          <div className="user-form-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="form-header">
              <div className="header">{data ? 'Update Recharge' : 'New Recharge Info'}</div>
              <CloseButton
                onClickhandle={() => {
                  resetImage();
                  if (data === null) {
                    setState({});
                    setValidation({});
                  }
                  setPreFileData({
                    photoUrl: data?.hotelThumbnailUrl ?? null,
                    fileName: data?.thumbnailFileName ?? null,
                  });
                  setPrevMultipleFileIds(data?.hotelImageIds ?? []);
                  setPrevMultiFileData(multipleFileNameWithImageUrls);
                  setImageDeleteList([]);
                  setErrorSingleMessage('');
                  setErrorMultiMessage('');
                  onClickSubmit();
                }}
              />
            </div>

            <div className="form-container">
              <div className="form-label-input">
                <label className="form-label">
                  {RechargeStringLabels.recharge}
                  <Required />
                </label>
                <div className="form-input-container">
                  <input
                    type="text"
                    value={state.name ?? ''}
                    name="name"
                    onChange={(e) => handleChange(e)}
                    className="form-input"
                    placeholder="Input"
                    required={true}
                  />
                </div>
                <ErrorMessageComponent message={validation.name} />
              </div>

              <div className="form-label-input">
                <label className="form-label">
                  {RechargeStringLabels.description}
                  <Required />
                </label>
                <div className="user-form-input-container">
                  <textarea
                    className="form-input"
                    type="text"
                    value={state.description ?? ''}
                    name="description"
                    onChange={(e) => handleChange(e)}
                    placeholder="Input"
                    rows={3}
                  />
                </div>
                <ErrorMessageComponent message={validation.description} />
              </div>

              <div className="form-label-input">
                <label className="form-label">
                  {RechargeStringLabels.location}
                  <Required />
                </label>
                <div className="form-input-container">
                  <input
                    className="form-input"
                    type="text"
                    value={state.location ?? ''}
                    name="location"
                    onChange={(e) => handleChange(e)}
                    placeholder="Input"
                    required={true}
                  />
                </div>
                <ErrorMessageComponent message={validation.location} />
              </div>

              <div className="form-label-input">
                <label className="form-label">
                  {RechargeStringLabels.address}
                  <Required />
                </label>
                <div className="form-input-container">
                  <input
                    className="form-input"
                    type="text"
                    name="address"
                    value={state.address ?? ''}
                    onChange={(e) => handleChange(e)}
                    placeholder="Input"
                    required={true}
                  />
                </div>
                <ErrorMessageComponent message={validation.address} />
              </div>

              <div className="form-label-input">
                <label className="form-label">
                  {RechargeStringLabels.latitude}
                  <Required />
                </label>
                <div className="form-input-container">
                  <input
                    className="form-input"
                    type="text"
                    name="latitude"
                    value={state.latitude ?? ''}
                    onChange={(e) => handleChange(e)}
                    placeholder="e.g. 12.9845"
                    required={true}
                  />
                </div>
                <ErrorMessageComponent message={validation.latitude} />
              </div>

              <div className="form-label-input">
                <label className="form-label">
                  {RechargeStringLabels.longitude}
                  <Required />
                </label>
                <div className="form-input-container">
                  <input
                    className="form-input"
                    type="text"
                    name="longitude"
                    value={state.longitude ?? ''}
                    onChange={(e) => handleChange(e)}
                    placeholder="e.g. 77.5846"
                    required={true}
                  />
                </div>
                <ErrorMessageComponent message={validation.longitude} />
              </div>

              <div className="form-label-input">
                <label className="form-label">
                  {RechargeStringLabels.drivingTime}
                  <Required />
                </label>
                <div className="form-input-container">
                  <input
                    className="form-input"
                    type="text"
                    name="drivingTime"
                    value={state.drivingTime ?? ''}
                    onChange={(e) => handleChange(e)}
                    placeholder="Input"
                    required={true}
                  />
                </div>
                <ErrorMessageComponent message={validation.drivingTime} />
              </div>

              <FileUploader
                previewShape="rectangle"
                headerName={RechargeStringLabels.thumbnail}
                handleDragOver={handleDrag}
                handleDrop={handleDrop}
                handleFileSelect={handleFileSelect}
                setPreFileData={setPreFileData}
                handleState={handleState}
                type="recharge"
              />
              {errorSingleMessage !== '' && <p className="error image-error">{errorSingleMessage}</p>}

              {prevFileData?.photoUrl && data?.hotelThumbnailUrl && data?.thumbnailFileName ? (
                <ImagePreviewWithFilename
                  photoUrl={data.hotelThumbnailUrl}
                  fileName={data.thumbnailFileName}
                  handleCancel={handleThumbnailCancel}
                />
              ) : null}

              <FileUploader
                previewShape="rectangle"
                headerName={RechargeStringLabels.photos}
                handleDragOver={handleDrag}
                handleDrop={handleMultiDrop}
                handleFileSelect={handleMultiFileSelect}
                isMultiUpload={true}
                setMultipleFileData={setPrevMultiFileData}
                handleMultiState={handleMultipleImageState}
                type="recharge"
              />
              {errorMultiMessage !== '' && <p className="error image-error">{errorMultiMessage}</p>}
              {Array.isArray(prevMultiFileData) &&
                prevMultiFileData.length >= 1 &&
                prevMultiFileData.map((url) => {
                  return (
                    <ImagePreviewWithFilename
                      key={url.id}
                      photoUrl={url.photoUrl}
                      fileName={url.fileName}
                      photoId={url.id}
                      handleCancel={handleImageCancel}
                    />
                  );
                })}

              <div className="footer-button-container">
                <CancelButton
                  onClickhandle={() => {
                    resetImage();
                    if (data === null) {
                      setState({});
                      setValidation({});
                    }
                    setPreFileData({
                      photoUrl: data?.hotelThumbnailUrl ?? null,
                      fileName: data?.thumbnailFileName ?? null,
                    });
                    setPrevMultipleFileIds(data?.hotelImageIds ?? []);
                    setPrevMultiFileData(multipleFileNameWithImageUrls);
                    setImageDeleteList([]);
                    setErrorSingleMessage('');
                    setErrorMultiMessage('');
                    onClickSubmit();
                  }}
                >
                  {RechargeStringLabels.cancel}
                </CancelButton>
                <BlackButton onClickhandle={() => handleNextButton()}>
                  {data ? RechargeStringLabels.update : RechargeStringLabels.add}
                </BlackButton>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AddHotelForm;